import * as airwallexSdk from '@airwallex/components-sdk';
import { ElementOptionsTypes, ElementTypes } from '@airwallex/components-sdk';
import {
  AccountTypeEnum,
  AirwallexAccountTypeEnum,
  AirwallexApi,
  AirwallexBeneficiaryDto,
  BankDetail,
  Configuration,
  CreateBankDetailDto,
  UpdateBankDetailDto,
} from '@sr-sdks/api-sdk-axios';
import { Button } from '@sr/ui-library';
import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { configuration } from '../../configuration';
import { reduxServices } from '../../reducers/reduxServices';
import { workspaceService } from '../../reducers/workspaces/workspaceSlice';
import { useAppDispatch, useTypedSelector } from '../../stateStore';
import axiosInstance from '../../utils/axiosInstance';
import {
  generateCodeChallengeFromVerifier,
  generateCodeVerifier,
} from '../../utils/helpers/airwallexCodeChallengeHelpers';
import { isLoadingState } from '../../utils/helpers/common';
import { getHslVarAsHex } from '../../utils/helpers/themeColors';
import { ConfirmButtons } from '../ConfirmButtons';
import AppLoader from '../Loader';

type AirwallexSubmitResult = {
  errors: string;
  values: AirwallexBeneficiaryDto;
};

type GetAuthCodeResponse = {
  authCode: string;
  clientId: string;
};

interface IAirwallexBeneficiaryFormProps {
  backButtonText?: string;
  bankDetail: BankDetail | null;
  cancelButtonText?: 'Cancel' | 'Skip for now';
  /**
   * Placeholder shown in place of the Airwallex loading spinner until the
   * hosted form fires `ready`. Pass the caller's own loading state so the wait
   * for the SDK looks like a continuation of the page's placeholder rather
   * than a third, differently-shaped loader.
   */
  formPlaceholder?: React.ReactNode;
  hasCancelButton?: boolean;
  /**
   * MSR3 onboarding step 3 (APP-2237). The page owns the heading and the action
   * row is right-aligned to match the other MSR3 onboarding steps; the Airwallex
   * fields themselves render in an iframe and are unaffected.
   */
  isMsr3?: boolean;
  isOnboarding?: boolean;
  isReadOnly?: boolean;
  isWorkspaceSettingsPage?: boolean;
  /**
   * Renders a left-aligned button that leaves the step without saving. Omitted
   * everywhere the form has nothing to go back to.
   */
  onBackClick?: () => void;
  onCancelClick?: () => void;
  onSaveSuccess?: () => void;
  onSkipClick?: () => void;
  saveButtonText?: 'Save & Continue' | 'Save';
  /**
   * Workspace the bank detail belongs to. MSR3 onboarding passes it from the
   * route so nothing has to load the workspace into Redux for this form's sake;
   * legacy callers omit it and keep reading the Redux workspace.
   */
  workspaceId?: string;
}

// Function for creating an instance of the AirwallexApi class
const GetAirwallexApi = () => {
  return new AirwallexApi(
    new Configuration(),
    configuration.PAYMENTS_API_BASE,
    axiosInstance,
  );
};

// Held outside the component to solve APP-1026
let beneficiaryForm: ElementTypes['beneficiaryForm'];

/** SR.app blue — the beneficiary form's accent everywhere outside MSR3. */
const LEGACY_PRIMARY_PALETTE = {
  '10': '#e3e8f7',
  '20': '#e3e8f7',
  '30': '#e3e8f7',
  '40': '#e3e8f7',
  '50': '#5673AF',
  '60': '#5673AF',
  '70': '#5673AF',
  '80': '#5673AF',
  '90': '#5673AF',
  '100': '#5673af',
};

/** Airwallex's own in-iframe spinner colour, used everywhere outside MSR3. */
const LEGACY_SPINNER_COLOR = '#2B3035';

/** The ui-library token this form dresses the iframe with, and its fallback. */
const MSR3_PRIMARY_VAR = '--ui-primary';
const MSR3_PRIMARY_FALLBACK_HEX = '#df2f1a';

/** How long a caller-supplied placeholder waits on the SDK's `ready` event. */
const FORM_READY_TIMEOUT_MS = 15_000;

/**
 * The fields render in an iframe, so the palette option is the only way to
 * bring the radios, focus rings and selected transfer method in line with the
 * rest of the MSR3 onboarding flow. Built from the resolved `--ui-primary`
 * rather than a transcribed hex, so a brand change in index.css reaches the
 * hosted form too.
 */
const msr3PrimaryPalette = (primary: string) => ({
  '10': '#fce9e7',
  '20': '#fce9e7',
  '30': '#fce9e7',
  '40': '#fce9e7',
  '50': primary,
  '60': primary,
  '70': primary,
  '80': primary,
  '90': primary,
  '100': primary,
});

const AirwallexBeneficiaryForm = ({
  backButtonText = 'Back',
  bankDetail,
  cancelButtonText = 'Cancel',
  formPlaceholder,
  hasCancelButton = true,
  isMsr3 = false,
  isOnboarding,
  isReadOnly = false,
  onBackClick,
  onCancelClick,
  onSaveSuccess,
  onSkipClick,
  saveButtonText = 'Save',
  workspaceId,
}: IAirwallexBeneficiaryFormProps) => {
  const dispatch = useAppDispatch();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const cancelledRef = useRef(false);
  const submitInFlightRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormReady, setIsFormReady] = useState(false);
  const revealTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const isCreating = useTypedSelector(
    reduxServices.bankDetails.selectors.isCreating,
  );

  const isUpdating = useTypedSelector(
    reduxServices.bankDetails.selectors.isUpdating,
  );

  const reduxWorkspace = useTypedSelector(workspaceService.selectors.workspace);
  const activeWorkspaceId = workspaceId ?? reduxWorkspace?._id;

  /**
   * Only callers that supplied a placeholder wait on `ready`, so the reset, the
   * timer and the SDK listeners below are theirs alone. A boolean rather than
   * the node itself: `formPlaceholder` is fresh JSX on every render and would
   * re-init the form from the effect's dependencies.
   */
  const hasFormPlaceholder = Boolean(formPlaceholder);

  const handleCancelClick = async () => {
    beneficiaryForm?.unmount();
    beneficiaryForm?.destroy();

    if (onSkipClick) {
      return onSkipClick();
    }

    onCancelClick?.();
  };

  /** Same teardown as cancel — the Airwallex element must not outlive the step. */
  const handleBackClick = () => {
    beneficiaryForm?.unmount();
    beneficiaryForm?.destroy();

    onBackClick?.();
  };

  const handleCreateBankDetails = async (
    createBankDetailDto: CreateBankDetailDto,
  ) => {
    if (!createBankDetailDto) return;

    // Create bankDetail record
    const createBankDetailsResult = await dispatch(
      reduxServices.bankDetails.createV2(createBankDetailDto),
    );

    if (
      reduxServices.bankDetails.createV2.fulfilled.match(
        createBankDetailsResult,
      )
    ) {
      onSaveSuccess?.();
    }
  };

  const handleInitBeneficiaryForm = useCallback(async () => {
    // Skip if container not mounted
    if (!containerRef.current || !containerRef.current.isConnected) return;

    /** Uncovers the placeholder, whether the form booted or gave up. */
    const revealForm = () => {
      clearTimeout(revealTimeoutRef.current);
      setIsFormReady(true);
    };

    if (hasFormPlaceholder) {
      setIsFormReady(false);

      /**
       * `ready` and `error` are the only signals the SDK gives, and an iframe
       * that never loads at all fires neither. Uncover regardless once the wait
       * stops being credible, so a stalled boot degrades to whatever Airwallex
       * is showing rather than leaving the cover over the fields for good.
       */
      revealTimeoutRef.current = setTimeout(revealForm, FORM_READY_TIMEOUT_MS);
    }

    async function initAirwallexComponent() {
      // Ensure any existing element is cleaned before creating a new one
      try {
        beneficiaryForm?.unmount?.();
        beneficiaryForm?.destroy?.();
      } catch {
        // no-op
      }

      // Embedded Beneficiary component
      // See https://www.airwallex.com/docs/payouts__embedded-beneficiary-component
      const codeVerifier = generateCodeVerifier();
      const codeChallenge =
        await generateCodeChallengeFromVerifier(codeVerifier);

      const authCodeResponse =
        await GetAirwallexApi().airwallexControllerGetAuthCode(codeChallenge);
      const authCodeData = {
        ...authCodeResponse.data,
      } as GetAuthCodeResponse;

      await airwallexSdk.init({
        authCode: authCodeData.authCode,
        clientId: authCodeData.clientId,
        codeVerifier: codeVerifier,
        env: configuration.AIRWALLEX_ENV as any,
        locale: 'en',
      });

      let defaultValues =
        bankDetail?.airwallexBeneficiary ||
        ({
          beneficiary: {
            bank_details: {
              account_currency: 'AUD',
              bank_country_code: 'AU',
              local_clearing_system: 'BANK_TRANSFER',
            },
            entity_type: AirwallexAccountTypeEnum.Company,
          },
          payment_methods: ['LOCAL'],
        } as AirwallexBeneficiaryDto);

      if (isReadOnly && bankDetail?.airwallexBeneficiary) {
        // Overwrite the default values in read-only mode
        // To mask account number when viewing in read-only mode
        // It's clunky, but this approach was apparently required... other approaches led to errors
        defaultValues = {
          ...defaultValues,
          beneficiary: {
            ...defaultValues.beneficiary,
            bank_details: {
              ...defaultValues.beneficiary.bank_details,
              account_number: bankDetail?.accountNumberMasked,
              iban: bankDetail?.accountNumberMasked,
            },
          },
        };
      }

      // Read at init rather than at module scope: a top-level getComputedStyle
      // would be a side effect on import, and the stylesheet may not be applied
      // that early.
      const msr3Primary = getHslVarAsHex(
        MSR3_PRIMARY_VAR,
        MSR3_PRIMARY_FALLBACK_HEX,
      );
      const spinnerColor = isMsr3 ? msr3Primary : LEGACY_SPINNER_COLOR;

      const options: ElementOptionsTypes['beneficiaryForm'] = {
        customizations: {
          fields: {
            'beneficiary.additional_info.personal_email': { hidden: true },
          },
          ui: {
            hideFeeConfig: !isReadOnly,
            hideFlightTime: !isReadOnly,
          },
        },
        defaultValues: defaultValues,
        theme: {
          components: {
            /**
             * The spinner Airwallex paints inside its iframe while the form
             * boots — the one wait this app cannot dress itself. MSR3 puts it
             * in brand red so it matches the placeholder it takes over from.
             */
            spinner: {
              colors: {
                start: { initial: spinnerColor },
                stop: { initial: spinnerColor },
              },
            },
          },
          palette: {
            primary: isMsr3
              ? msr3PrimaryPalette(msr3Primary)
              : LEGACY_PRIMARY_PALETTE,
          },
        },
      };

      // This method creates an instance of the component
      // See https://github.com/airwallex/payouts-web-sdk#create-element
      const element = await airwallexSdk.createElement(
        'beneficiaryForm',
        options,
      );

      /**
       * `ready` is the SDK's own "loading indicators can go now" signal — the
       * only point at which the hosted form is actually on screen, so the only
       * honest moment to uncover it. `error` means it never will be, and a
       * cover left over a form that failed to boot would hide the failure.
       */
      if (hasFormPlaceholder) {
        element.on?.('ready', revealForm);
        element.on?.('error', revealForm);
      }

      // Only mount if still mounted and container exists
      if (cancelledRef.current) return;

      const container = containerRef.current;

      if (!container || !container.isConnected) return;

      try {
        element.mount(container);
        beneficiaryForm = element;
      } catch {
        // If mounting fails due to DOM issues, ensure cleanup
        revealForm();

        try {
          element.unmount?.();
          element.destroy?.();
        } catch {
          // no-op
        }
      }
    }

    await initAirwallexComponent();
  }, [bankDetail, hasFormPlaceholder, isMsr3, isReadOnly]);

  useEffect(() => {
    cancelledRef.current = false;
    handleInitBeneficiaryForm();

    return () => {
      cancelledRef.current = true;

      clearTimeout(revealTimeoutRef.current);

      try {
        beneficiaryForm?.unmount?.();
        beneficiaryForm?.destroy?.();
      } catch {
        // no-op
      }

      // reset to undefined
      beneficiaryForm = undefined as any;
    };
  }, [bankDetail, handleInitBeneficiaryForm]);

  const handleUpdateBankDetails = async (
    updateBankDetailDto: UpdateBankDetailDto,
  ) => {
    if (!updateBankDetailDto || !bankDetail) return;

    // Update bankDetail record
    const updateBankDetailsResult = await dispatch(
      reduxServices.bankDetails.updateV2({
        id: bankDetail._id,
        updateBankDetailDto,
      }),
    );

    if (
      reduxServices.bankDetails.updateV2.fulfilled.match(
        updateBankDetailsResult,
      )
    ) {
      onSaveSuccess?.();
    }
  };

  const handleSaveClick = async () => {
    if (!activeWorkspaceId || submitInFlightRef.current) return;

    submitInFlightRef.current = true;
    setIsSubmitting(true);

    try {
      const formData =
        (await beneficiaryForm?.submit()) as unknown as AirwallexSubmitResult;

      // Errors will be shown inline by the beneficiary form, so just stop here to prevent execution if there are any errors recorded
      if (formData.errors) {
        return;
      }

      const beneficiary = formData.values.beneficiary;

      if (
        !beneficiary.bank_details.account_number &&
        !beneficiary.bank_details.iban
      ) {
        // Heuristic: if neither account number nor IBAN are provided, then the request would fail
        // An alert is already shown in the beneficiary form about unsupported country/currency selections
        return;
      }

      const payload = {
        accountType:
          beneficiary.entity_type === AirwallexAccountTypeEnum.Company
            ? AccountTypeEnum.Business
            : AccountTypeEnum.Personal,
        airwallexBeneficiary: formData.values,
        bankCountry: beneficiary.bank_details.bank_country_code,
        currency: beneficiary.bank_details.account_currency,
        workspaceId: activeWorkspaceId,
      };

      if (bankDetail) {
        // Existing - Update
        await handleUpdateBankDetails(payload);
      } else {
        // Create
        await handleCreateBankDetails(payload);
      }
    } finally {
      submitInFlightRef.current = false;
      setIsSubmitting(false);
    }
  };

  const isSaveLoading =
    isSubmitting || isLoadingState(isCreating) || isLoadingState(isUpdating);

  /** Only callers that supplied a placeholder wait on `ready`. */
  const showPlaceholder = Boolean(formPlaceholder) && !isFormReady;

  return (
    <>
      {isOnboarding && <h2>Provide Bank Details for Payment</h2>}
      <form
        id="bankdetails-form"
        className={clsx({
          'cursor-not-allowed': isReadOnly,
          'flex w-full flex-col gap-5': true,
        })}
        onSubmit={(e) => e.preventDefault()}
      >
        {isReadOnly && (
          // Make this the first tabbable element
          // And prevent keyboard events when in read-only mode
          // This is intentional to block interactivity for staff (see APP-978)
          <a tabIndex={0} onKeyDown={(e) => isReadOnly && e.preventDefault()} />
        )}
        {/*
         * The placeholder covers the container rather than resizing or
         * replacing it: the iframe has to lay out at full size to boot at all,
         * and it paints a skeleton of its own in Airwallex's grey that would
         * otherwise sit under ours in a second palette. min-h holds the cover
         * open over the container's own empty height, before the iframe that
         * gives it a size exists.
         */}
        <div className={clsx('relative', showPlaceholder && 'min-h-80')}>
          {/* bg-card, not bg-background: the cover sits inside the step's own
              card, so it has to be that card's surface or it paints a grey
              slab across a white one. */}
          {showPlaceholder && (
            <div className="absolute inset-0 z-10 overflow-hidden bg-card">
              {formPlaceholder}
            </div>
          )}

          <div
            id="beneficiary-form-container"
            className={clsx({
              // App.css bleeds this container 12px past its parent on both
              // sides. Inside the MSR3 card that eats the card padding, so
              // cancel it — `!` is needed to beat the id selector in App.css.
              '!mx-0': isMsr3,
              // Prevent pointer events when in read-only mode
              // This is intentional to block interactivity for staff (see APP-978)
              'pointer-events-none': isReadOnly,
            })}
            ref={containerRef}
          >
            {!formPlaceholder && <AppLoader.Overlay />}
          </div>
        </div>
        {isReadOnly ? (
          <ConfirmButtons
            className="my-5"
            isLoading={isSaveLoading}
            saveButtonProps={{
              children: 'Close',
              onClick: handleCancelClick,
            }}
          />
        ) : (
          <div
            className={clsx(
              // Only the Back button needs the row. Without one, wrapping would
              // turn ConfirmButtons from a block-level child of the form into a
              // flex item, which resizes its own `max-sm` stack on the legacy
              // screens this PR otherwise leaves byte-identical.
              onBackClick && 'flex items-center gap-4',
              // Legacy keeps its centred action row; only MSR3 splits Back to
              // the left from Skip / Save on the right.
              onBackClick && (isMsr3 ? 'justify-between' : 'justify-center'),
              /*
               * MSR3 only: below `sm` the nested ConfirmButtons stacks itself,
               * so leaving this a row left Back beside a column of Skip / Save
               * (APP-2237, QA note 7). Stacking here puts all three in one
               * full-width column like the other onboarding steps.
               */
              onBackClick && isMsr3 && 'max-sm:flex-col max-sm:items-stretch',
            )}
          >
            {onBackClick && (
              <Button
                disabled={isSubmitting}
                variant="outline"
                onClick={handleBackClick}
              >
                {backButtonText}
              </Button>
            )}

            <ConfirmButtons
              cancelButtonProps={{
                children: cancelButtonText,
                disabled: isSubmitting,
                onClick: handleCancelClick,
              }}
              /*
               * `ml-auto` only from `sm` up: in the stacked column it would
               * opt this block out of the row's stretch and pin it right,
               * leaving Skip / Save narrower than Back.
               *
               * `my-5` off in the stacked column too: it stacks on top of the
               * row's own `gap-4`, so Back sat 36px clear of Skip while the
               * other MSR3 steps use a flat 16px (APP-2237, QA note 7).
               */
              className={clsx('my-5', {
                'justify-end max-sm:my-0 sm:ml-auto': isMsr3,
              })}
              hasCancelButton={hasCancelButton}
              isLoading={isSaveLoading}
              saveButtonProps={{
                children: saveButtonText,
                onClick: handleSaveClick,
              }}
            />
          </div>
        )}
      </form>
    </>
  );
};

export default AirwallexBeneficiaryForm;
